import { failingFetcher } from '@lib/http/fetch';
import * as Sentry from '@sentry/nextjs';
import { CoveoSearchHub } from './coveoConstants';

const SEARCH_TOKEN_LOCAL_STORAGE_KEY = (searchHub: CoveoSearchHub) =>
  `coveoSearchToken-${searchHub}`;

export type TokenStep = 'token_fetch' | 'token_renew';

/**
 * Calls the server to request a new Coveo search token, and saves the new token to localStorage.
 *
 * When `rethrowOnError` is true, the function skips its internal Sentry capture and rethrows the
 * underlying error so the caller can attach its own scope/tags before reporting. Used by
 * `createEngineModule.doInit` so the boundary owns a single, fully-tagged Sentry event per failure.
 *
 * `step` controls the Sentry tag for the helper's own captures: pass `'token_renew'` when invoked
 * from a Coveo SDK `renewAccessToken` callback so renewal failures don't masquerade as init failures.
 */
export const getNewSearchToken = async (
  searchHub: CoveoSearchHub,
  {
    rethrowOnError = false,
    step = 'token_fetch',
  }: { rethrowOnError?: boolean; step?: TokenStep } = {}
): Promise<string | null> => {
  try {
    const searchToken = await failingFetcher(
      `/api/coveo/search-token?searchHub=${searchHub}`
    );

    if (searchToken && typeof searchToken === 'string') {
      setSavedSearchToken(searchHub, JSON.stringify(searchToken));
      return searchToken;
    }

    const invalidResponseError = new Error(
      `Error in getNewSearchToken: invalid response (type=${typeof searchToken})`
    );
    if (rethrowOnError) throw invalidResponseError;

    Sentry.withScope((scope) => {
      scope.setTags({
        'coveo.hub': searchHub,
        'coveo.step': step,
      });
      scope.setContext('coveo_init', {
        hub: searchHub,
        reason: 'invalid_response',
      });
      Sentry.captureException(invalidResponseError);
    });
  } catch (error) {
    if (rethrowOnError) throw error;

    Sentry.withScope((scope) => {
      scope.setTags({
        'coveo.hub': searchHub,
        'coveo.step': step,
      });
      scope.setContext('coveo_init', {
        hub: searchHub,
        reason: 'fetch_threw',
      });
      Sentry.captureException(error);
    });
  }

  return null;
};

/** Grabs the coveo search token from localStorage */
export const getSavedSearchToken = (
  searchHub: CoveoSearchHub
): string | null => {
  return localStorage?.getItem(SEARCH_TOKEN_LOCAL_STORAGE_KEY(searchHub));
};

/** Saves the coveo search token to localStorage */
export const setSavedSearchToken = (
  searchHub: CoveoSearchHub,
  newToken: string
): void => {
  localStorage?.setItem(SEARCH_TOKEN_LOCAL_STORAGE_KEY(searchHub), newToken);
};

/** Removes the coveo search token from localStorage */
export const resetSearchTokens = () => {
  localStorage?.removeItem(
    SEARCH_TOKEN_LOCAL_STORAGE_KEY(CoveoSearchHub.SEARCH)
  );
  localStorage?.removeItem(
    SEARCH_TOKEN_LOCAL_STORAGE_KEY(CoveoSearchHub.LISTING)
  );
};

export const getSearchTokenGroups = (searchToken: string | null) => {
  if (!searchToken) {
    return;
  }
  const base64Url = searchToken.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  const parsedToken = JSON.parse(window.atob(base64));

  return parsedToken?.userGroups as Array<string>;
};
